import {
	DateRangePicker,
	ExportRequestModal,
	ExportSuccessModal,
	FormFieldControl,
	MultiSelect,
} from "@components";
import { useQuery } from "@tanstack/react-query";
import { SearchLg } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import type { DateValue, Selection } from "react-aria-components";
import type { SelectItemType } from "@/shared/components/select/select-shared";
import { useExportInfractions } from "@/shared/hooks";
import { REPORT_TYPE } from "@/shared/types/generate-report-request";
import {
	buildGenerateReportRequest,
	formatDateValueToApi,
	normalizeExportFilterIds,
	selectionToNumberIds,
	validateExportDateRange,
} from "@/shared/utils";
import { mapInfractionVehicleLookupToSelectItem } from "../handlers/infraction-create-form.helpers";
import { getInfractionVehiclesLookup } from "../services/infractions.service";

type DateRangeValue = {
	start: DateValue;
	end: DateValue;
} | null;

type InfractionsExportModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

export function InfractionsExportModal({
	isOpen,
	onClose,
}: InfractionsExportModalProps) {
	const [selectedVehicleIds, setSelectedVehicleIds] = useState<number[]>([]);
	const [dateRange, setDateRange] = useState<DateRangeValue>(null);
	const [pullAllSinceStart, setPullAllSinceStart] = useState(false);
	const [vehiclesError, setVehiclesError] = useState<string | null>(null);
	const [dateError, setDateError] = useState<string | null>(null);
	const [isSuccessOpen, setIsSuccessOpen] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	const { data: vehiclesLookup = [] } = useQuery({
		queryKey: ["infractions", "vehicles-lookup", "export"],
		queryFn: getInfractionVehiclesLookup,
		enabled: isOpen,
	});

	const vehicleItems = useMemo<SelectItemType[]>(
		() =>
			vehiclesLookup
				.map(mapInfractionVehicleLookupToSelectItem)
				.map((item) => ({
					id: String(item.value),
					label: item.label,
				})),
		[vehiclesLookup]
	);

	const allVehicleIds = useMemo(
		() => vehicleItems.map((item) => Number(item.id)),
		[vehicleItems]
	);

	const selectedKeys = useMemo(
		() => new Set<string>(selectedVehicleIds.map(String)),
		[selectedVehicleIds]
	);

	const exportMutation = useExportInfractions({
		onSuccess: (response) => {
			onClose();
			setSuccessMessage(response.message);
			setIsSuccessOpen(true);
		},
	});

	useEffect(() => {
		if (!isOpen) {
			setSelectedVehicleIds([]);
			setDateRange(null);
			setPullAllSinceStart(false);
			setVehiclesError(null);
			setDateError(null);
		}
	}, [isOpen]);

	const handleVehiclesSelectionChange = (keys: Selection) => {
		setSelectedVehicleIds(selectionToNumberIds(keys, allVehicleIds));
		setVehiclesError(null);
	};

	const handleSubmit = () => {
		let hasError = false;

		const validationError = validateExportDateRange(
			dateRange?.start,
			dateRange?.end,
			{ pullAllSinceStart, isRequired: false }
		);
		if (validationError) {
			setDateError(validationError);
			hasError = true;
		} else {
			setDateError(null);
		}

		if (hasError) {
			return;
		}

		exportMutation.mutate(
			buildGenerateReportRequest({
				type: REPORT_TYPE.Infractions,
				initialDate: formatDateValueToApi(dateRange?.start),
				finalDate: formatDateValueToApi(dateRange?.end),
				pullAllSinceStart,
				vehicleIds: normalizeExportFilterIds(selectedVehicleIds, allVehicleIds),
			})
		);
	};

	return (
		<>
			<ExportRequestModal
				description="Digite a placa do veículo e selecione a data de cometimento"
				isOpen={isOpen}
				isSubmitting={exportMutation.isPending}
				onClose={onClose}
				onSubmit={handleSubmit}
				title="Exportar Infrações"
			>
				<FormFieldControl
					className="xs:grid xs:grid-cols-[110px_minmax(0,1fr)] xs:items-start xs:gap-4"
					error={vehiclesError ?? undefined}
					htmlFor="infractions-export-vehicles"
					label="Veículos"
				>
					<MultiSelect
						aria-label="Selecionar veículos"
						className="w-full"
						emptyStateDescription="Tente outro termo de busca."
						emptyStateTitle="Nenhum veículo encontrado"
						icon={SearchLg}
						isInvalid={!!vehiclesError}
						items={vehicleItems}
						onSelectionChange={handleVehiclesSelectionChange}
						placeholder="Digite ou selecione as placas dos veículos"
						popoverClassName="z-[100000]"
						selectedCountFormatter={(count) =>
							count === 1 ? "1 veículo" : `${count} veículos`
						}
						selectedKeys={selectedKeys}
						showFooter={false}
					>
						{(item) => (
							<MultiSelect.Item
								id={item.id}
								label={item.label}
								textValue={item.label}
							/>
						)}
					</MultiSelect>
				</FormFieldControl>

				<FormFieldControl
					className="xs:grid xs:grid-cols-[110px_minmax(0,1fr)] xs:items-start xs:gap-4"
					error={dateError ?? undefined}
					htmlFor="infractions-export-date"
					label="Data de cometimento"
				>
					<DateRangePicker
						aria-label="Período por data de cometimento das infrações"
						buttonClassName="w-full justify-start px-4"
						className="w-full"
						onChange={(range) => {
							setDateRange(range);
							setDateError(null);
						}}
						onPullAllSinceStartChange={(value) => {
							setPullAllSinceStart(value);
							if (value) {
								setDateRange(null);
							}
							setDateError(null);
						}}
						pullAllSinceStart={pullAllSinceStart}
						showPullAllSinceStart
						value={dateRange}
					/>
				</FormFieldControl>
			</ExportRequestModal>

			<ExportSuccessModal
				isOpen={isSuccessOpen}
				message={successMessage}
				onClose={() => setIsSuccessOpen(false)}
			/>
		</>
	);
}
